import { ADMIN_SESSION_KEY, postLoginPath } from '../admin/adminUtils';
import { customerApiErrorMessage } from '../storefront/customerFacingErrors';
import { getStorefrontSessionKey } from '../storefront/storefrontEvents';

export const LOGIN_GUARD_KEY = 'saloni_auth_login_guard_until';

export function authSessionGetUrl(token) {
  // Token travels in headers only (authSessionGetHeaders) — never in the query string.
  void token;
  return '/api/admin_auth.php';
}

export function authSessionGetHeaders(token) {
  const t = String(token || '').trim();
  return {
    'X-Admin-Session': t,
    Authorization: `Bearer ${t}`,
  };
}

export function readStoredSessionToken() {
  if (typeof window === 'undefined') return '';
  const session = sessionStorage.getItem(ADMIN_SESSION_KEY) || '';
  const local = localStorage.getItem(ADMIN_SESSION_KEY) || '';
  return session || local || '';
}

export function writeStoredSessionToken(token, remember = false) {
  const t = String(token || '').trim();
  if (!t) return false;
  try {
    sessionStorage.setItem(ADMIN_SESSION_KEY, t);
    if (remember) localStorage.setItem(ADMIN_SESSION_KEY, t);
    else localStorage.removeItem(ADMIN_SESSION_KEY);
    return readStoredSessionToken() === t;
  } catch {
    return false;
  }
}

export function clearStoredSessionToken() {
  try {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    localStorage.removeItem(ADMIN_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function setLoginGuardMs(ms = 30000) {
  try {
    sessionStorage.setItem(LOGIN_GUARD_KEY, String(Date.now() + ms));
  } catch {
    /* ignore */
  }
}

export function isLoginGuardActive() {
  try {
    return Date.now() < Number(sessionStorage.getItem(LOGIN_GUARD_KEY) || 0);
  } catch {
    return false;
  }
}

export function clearLoginGuard() {
  try {
    sessionStorage.removeItem(LOGIN_GUARD_KEY);
  } catch {
    /* ignore */
  }
}

/** Extract JSON even when PHP warnings prepend output. */
export async function parseJsonResponse(response) {
  const text = await response.text();
  const trimmed = String(text || '').replace(/^\uFEFF/, '').trim();
  if (!trimmed) {
    return { ok: false, error: 'Empty response from server.' };
  }
  try {
    return { ok: true, data: JSON.parse(trimmed) };
  } catch {
    const start = trimmed.indexOf('{');
    const end = trimmed.lastIndexOf('}');
    if (start >= 0 && end > start) {
      try {
        return { ok: true, data: JSON.parse(trimmed.slice(start, end + 1)) };
      } catch {
        /* fall through */
      }
    }
    const looksLikePhpSource =
      trimmed.startsWith('<?php') || trimmed.includes('<?php') || trimmed.includes('declare(strict_types=1)');
    return {
      ok: false,
      error: customerApiErrorMessage(
        looksLikePhpSource ? 'Invalid server response.' : 'Invalid JSON from server.',
        'Something went wrong. Please try again.'
      ),
    };
  }
}

export async function postLoginRequest(username, password) {
  const r = await fetch('/api/admin_auth.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'login', username, password, session_key: getStorefrontSessionKey() || '' }),
    cache: 'no-store',
  });
  const parsed = await parseJsonResponse(r);
  if (!parsed.ok) return { ok: false, error: customerApiErrorMessage(parsed.error, 'Sign-in failed.'), status: r.status };
  const d = parsed.data || {};
  if (!r.ok || !d.success) {
    let err = customerApiErrorMessage(d.error, 'Sign-in failed.');
    if (r.status === 429) {
      const sec = Number(d.retry_after || 0);
      const mins = sec > 0 ? Math.max(1, Math.ceil(sec / 60)) : 0;
      err = mins
        ? `Too many failed sign-in attempts. Wait about ${mins} minute${mins === 1 ? '' : 's'}, then try again.`
        : err;
    }
    return { ok: false, error: err, status: r.status };
  }
  const token = String(d.session_token || '').trim();
  if (!token) return { ok: false, error: 'No session returned.', status: r.status };
  return { ok: true, data: d, token, status: r.status };
}

export async function fetchSessionProfile(token) {
  const t = String(token || '').trim();
  if (!t) return { ok: false, error: 'No session token.' };
  try {
    const r = await fetch(authSessionGetUrl(t), {
      headers: authSessionGetHeaders(t),
      cache: 'no-store',
    });
    const parsed = await parseJsonResponse(r);
    if (!parsed.ok) return { ok: false, error: customerApiErrorMessage(parsed.error, 'Session could not be verified.') };
    const d = parsed.data || {};
    if (!r.ok || !d.logged_in) {
      return { ok: false, error: customerApiErrorMessage(d.error, 'Session not recognized.') };
    }
    return { ok: true, data: d };
  } catch {
    return {
      ok: false,
      error: customerApiErrorMessage('', 'Could not verify session. Please try again.'),
    };
  }
}

export function hardNavigateToPath(path) {
  const dest = String(path || '/').trim() || '/';
  const url = dest.startsWith('http') ? dest : `${window.location.origin}${dest}`;
  window.location.href = url;
}

export function hardNavigateAfterLogin(role, nextRaw = '') {
  hardNavigateToPath(postLoginPath(role, nextRaw));
}
